import React, { useState } from "react";
import { useTranslate } from "react-admin";

import { useExportColumns } from "../../../hooks/useExportColumns";
import { useExportColumnsSelection } from "../../../hooks/useExportColumnsSelection";
import { formatISOWithoutTimeZone } from "../../_helpers/dateTime";
import { CompanyFilterField } from "../ExportDialog/CompanyFilterField";
import { DateFilterField } from "../ExportDialog/DateFilterField";
import { ExportColumnsButton } from "../ExportDialog/ExportColumnsButton";
import { ExportModalBase } from "../ExportDialog/ExportModalBase";
import { ExportTriggerButton } from "../ExportDialog/ExportTriggerButton";
import { StatusFilterField } from "../ExportDialog/StatusFilterField";
import { useSimpleModal } from "../useSimpleModal";
import { CategoryFilterField } from "./CategoryFilterField";
import { TypeFilterField } from "./TypeFilterField";

export const DeviationsExportWithFilters = ({ filters }) => {
	const exportModal = useSimpleModal();
	const endpoint = "deviations";
	return (
		<React.Fragment>
			<ExportTriggerButton label="resources.deviations.export" isLoading={false} onClick={exportModal.handleOpen} />
			{exportModal.opened && (
				<ExportModalBase
					filters={filters}
					endpoint={endpoint}
					onModalClose={exportModal.handleClose}
				/>
			)}
		</React.Fragment>
	);
};

export const DeviationsExportView = ({
	viewComponentProps,
	initDateFrom,
	columnsChoices,
	columns,
	setHeaderFilter,
	ViewComponent = ExportModalBase,
}) => {
	const tableRef = React.useRef(null);
	const translate = useTranslate();

	const [exportDate, setExportDate] = React.useState(null);
	const [dateFromFilter, setDateFromFilter] = React.useState(initDateFrom);
	const [dateToFilter, setDateToFilter] = React.useState(null);
	const [statusId, setStatusId] = React.useState(undefined);
	const [customerId, setCustomerId] = React.useState(undefined);
	const [categoryId, setCategoryId] = React.useState(undefined);
	const [typeId, setTypeId] = React.useState(undefined);
	const { columnsSelected, setColumnsSelected, excludedColumns } =
		useExportColumnsSelection(columns);

	React.useEffect(() => {
		if (categoryId === undefined) {
			setTypeId(undefined);
		}
	}, [categoryId]);

	const endpoint = "deviations";

	const filters = React.useMemo(() => {
		const strDateFromFilter = formatISOWithoutTimeZone(dateFromFilter);
		const strDateToFilter = formatISOWithoutTimeZone(dateToFilter);

		const filtersObj = {
			excluded_columns: excludedColumns,
		};

		if (strDateFromFilter) filtersObj["created_at:gt"] = strDateFromFilter;
		if (strDateToFilter) filtersObj["created_at:lt"] = strDateToFilter;
		if (statusId !== undefined) filtersObj.status = statusId;
		if (customerId !== undefined) filtersObj.company_id = customerId;
		if (categoryId !== undefined) filtersObj.deviation_main_category = categoryId;
		if (typeId !== undefined) filtersObj.deviation_type = typeId;

		return filtersObj;
	}, [dateFromFilter, dateToFilter, statusId, customerId, categoryId, typeId, excludedColumns]);

	React.useEffect(() => {
		setHeaderFilter({
			company_id: customerId,
		});
	}, [customerId]);

	const titleButtons = [
		<CategoryFilterField value={categoryId} onChange={setCategoryId} />,
		<TypeFilterField value={typeId} onChange={setTypeId} categoryValue={categoryId} />,
		<StatusFilterField
			resource="deviation-statuses"
			value={statusId}
			onChange={setStatusId}
			label={translate("resources.deviations.export-dialog.fields.status")}
		/>,
		<CompanyFilterField
			value={customerId}
			onChange={setCustomerId}
			label={translate("resources.deviations.export-dialog.fields.customer")}
		/>,
		<DateFilterField
			label={translate("resources.deviations.export-dialog.fields.date_from")}
			onChange={setDateFromFilter}
			value={dateFromFilter}
		/>,
		<DateFilterField
			label={translate("resources.deviations.export-dialog.fields.date_to")}
			onChange={setDateToFilter}
			value={dateToFilter}
		/>,
		<ExportColumnsButton
			choices={columnsChoices}
			value={columnsSelected}
			onChange={setColumnsSelected}
		/>,
	];

	return (
		<ViewComponent
			filters={filters}
			endpoint={endpoint}
			{...viewComponentProps}
			titleButtons={titleButtons}
			setExportDate={setExportDate}
		/>
	);
};

export const DeviationsExportModal = () => {
	const exportModal = useSimpleModal();
	const [initDateFrom, setInitDateFrom] = useState(new Date(Date.now()));
	const { isLoading, columns, columnsChoices, loadColumns } = useExportColumns(
		"deviations-export-header"
	);
	const [headerFilter, setHeaderFilter] = useState({});

	const onOpen = async () => {
		const filter = { date_from: initDateFrom };
		setHeaderFilter(filter);
		await loadColumns(filter);
		exportModal.handleOpen();
	};

	React.useEffect(() => {
		if (exportModal.opened) {
			loadColumns(headerFilter);
		}
	}, [headerFilter]);

	return (
		<>
			<ExportTriggerButton label="osfc.buttons.export_search" isLoading={isLoading} onClick={onOpen} />

			{exportModal.opened && (
				<DeviationsExportView
					viewComponentProps={{ onModalClose: exportModal.handleClose }}
					initDateFrom={initDateFrom}
					columns={columns}
					setHeaderFilter={setHeaderFilter}
					columnsChoices={columnsChoices}
				/>
			)}
		</>
	);
};
